/**
 * Utility functions for P.O. Expenses
 */

// Helper function to format date as "1st Jan 26"
export const formatDateOrdinal = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    
    // Get ordinal suffix
    const getOrdinalSuffix = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  } catch (e) {
    return dateStr;
  }
};

// Helper function to format date as DD/MM/YY
export const formatDateDDMMYY = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateStr;
  }
};

// Helper function to format currency with commas (e.g., 17,525.89)
export const formatCurrency = (amount, decimals = 2) => {
  const num = parseFloat(amount) || 0;
  return num.toLocaleString('en-GB', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Helper to sort expenses by P.O. number numerically (descending - highest first)
export const sortByPONumber = (expenses) => {
  return [...expenses].sort((a, b) => {
    const numA = parseInt(a.po_number?.replace('30X', '') || '0');
    const numB = parseInt(b.po_number?.replace('30X', '') || '0');
    return numB - numA; // Descending order (newest/highest first)
  });
};

// Helper to calculate totals for the summary bar
export const calculateTotals = (expenses) => {
  const totals = {
    totalExpenses: 0,           // Sum of all P.O.s total_cost
    totalExTransport: 0,        // Sum of cost for branch_expense type only
    totalRecharges: 0,          // Sum of customer_recharge_amount
    totalHORecharge: 0,         // Sum of ho_recharge_amount
    totalExTransportNet: 0,     // Ex transport cost minus both recharges
    // Additional breakdown categories
    totalStaff: 0,
    totalCVHire: 0,
    totalMaintenance: 0,
    totalStationary: 0,
    totalOther: 0,
  };
  
  expenses.forEach(expense => {
    const cost = expense.total_cost || 0;
    const customerRecharge = expense.customer_recharge_amount || 0;
    const hoRecharge = expense.ho_recharge_amount || 0;
    const poType = expense.po_type || '';
    
    totals.totalExpenses += cost;
    totals.totalRecharges += customerRecharge;
    totals.totalHORecharge += hoRecharge;
    
    // Only sum Ex Transport for branch_expense type
    if (poType === 'branch_expense') {
      totals.totalExTransport += cost;
      totals.totalExTransportNet += (cost - customerRecharge - hoRecharge);
    }
    
    // Sum by category type
    if (poType === 'staff_cv_hire' || poType === 'temp_staff') {
      totals.totalStaff += cost;
    } else if (poType === 'cv_hire') {
      totals.totalCVHire += cost;
    } else if (poType === 'maintenance') {
      totals.totalMaintenance += cost;
    } else if (poType === 'stationery') {
      totals.totalStationary += cost;
    } else if (poType === 'other') {
      totals.totalOther += cost;
    }
  });
  
  return totals;
};

// Helper to calculate P&L for a single expense
export const calculatePL = (expense) => {
  const cost = expense.total_cost || 0;
  const recharge = (expense.customer_recharge_amount || 0) + (expense.ho_recharge_amount || 0);
  return cost - recharge;
};

// Helper to group expenses by month
export const groupExpensesByMonth = (expenses) => {
  const groups = {};
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  
  expenses.forEach(expense => {
    const date = new Date(expense.order_date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthName = date.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
    
    // Determine if this is current month, last month, or older
    const isCurrentMonth = date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    const isLastMonth = (date.getMonth() === currentMonth - 1 && date.getFullYear() === currentYear) ||
                        (currentMonth === 0 && date.getMonth() === 11 && date.getFullYear() === currentYear - 1);
    
    if (!groups[monthKey]) {
      groups[monthKey] = {
        key: monthKey,
        name: monthName,
        expenses: [],
        isCurrentMonth,
        isLastMonth,
        isOlder: !isCurrentMonth && !isLastMonth
      };
    }
    groups[monthKey].expenses.push(expense);
  });
  
  // Sort by date descending (newest first) and sort expenses within each group
  return Object.values(groups)
    .sort((a, b) => b.key.localeCompare(a.key))
    .map(group => ({
      ...group,
      expenses: sortByPONumber(group.expenses)
    }));
};

// Helper to filter expenses by date range
export const filterExpensesByDate = (expenses, startDate, endDate) => {
  if (!startDate && !endDate) return expenses;
  
  return expenses.filter(expense => {
    const expenseDate = new Date(expense.order_date);
    if (startDate && expenseDate < new Date(startDate)) return false;
    if (endDate && expenseDate > new Date(endDate + 'T23:59:59')) return false;
    return true;
  });
};

// Format a local Date as YYYY-MM-DD without UTC shift
export const toLocalDateString = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Helper to get current month date range
export const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: toLocalDateString(start),
    end: toLocalDateString(end)
  };
};
